import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { GeometricBackground } from '../components/GeometricBackground';
import { KhatimStarLogo } from '../components/AalimLogo';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(formData.email, formData.password);
            toast.success('Welcome back!', {
                description: 'Assalamu Alaikum, you have logged in successfully.'
            });
            navigate('/chat');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to login. Please check your credentials.';
            toast.error('Login Failed', { description: message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side - Pattern */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-muted overflow-hidden">
                <GeometricBackground />
                <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        {/* Logo - larger size with reduced gap like landing page */}
                        <div className="flex justify-center -mb-20">
                            <KhatimStarLogo size={280} className="mx-auto" />
                        </div>
                        <h2 className="font-heading text-3xl font-semibold text-foreground mb-4">
                            Welcome Back to aalim.ai
                        </h2>
                        <p className="font-arabic text-2xl text-primary mb-4" dir="rtl">
                            بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ
                        </p>
                        <p className="text-muted-foreground max-w-md">
                            "Seek knowledge from the cradle to the grave."
                            <br />
                            <span className="text-sm">— Islamic Proverb</span>
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 bg-background">
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <Card className="border-border">
                        <CardHeader className="text-center">
                            <div className="lg:hidden flex justify-center mb-4">
                                <KhatimStarLogo size={200} />
                            </div>
                            <CardTitle className="font-heading text-2xl">Login</CardTitle>
                            <CardDescription>
                                Enter your credentials to access your account
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            className="pl-10"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            data-testid="login-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            className="pl-10 pr-10"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            data-testid="login-password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <Button 
                                    type="submit" 
                                    className="w-full bg-primary hover:bg-primary/90"
                                    disabled={loading}
                                    data-testid="login-submit"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Logging in...
                                        </>
                                    ) : (
                                        'Login'
                                    )}
                                </Button>
                            </form>

                            <div className="mt-4 text-center">
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-muted-foreground hover:text-primary"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot your password?
                                </Link>
                            </div>

                            <div className="mt-6 text-center">
                                <p className="text-sm text-muted-foreground">
                                    Don't have an account?{' '}
                                    <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                                        Register here
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
